/**
 * Automatic Chunk Load Error Handler
 * 
 * This script detects chunk loading failures (common with code-split JavaScript apps)
 * and automatically clears the cache and reloads the page for the user.
 * 
 * Installation: Add this script early in your <head> tag, before other scripts load
 */

(function() {
    'use strict';
    
    // Configuration
    const CONFIG = {
        storageKey: 'chunkErrorReloadAttempt',
        maxReloadAttempts: 2, // Prevent infinite reload loops
        reloadDelay: 500 // ms delay before reload
    };

    /**
     * Check if we've already tried reloading recently
     */
    function shouldAttemptReload() {
        try {
            const lastAttempt = sessionStorage.getItem(CONFIG.storageKey);
            
            if (!lastAttempt) {
                return true;
            }
            
            const attemptData = JSON.parse(lastAttempt);
            const timeSinceLastAttempt = Date.now() - attemptData.timestamp;
            
            // Reset after 5 minutes
            if (timeSinceLastAttempt > 5 * 60 * 1000) {
                sessionStorage.removeItem(CONFIG.storageKey);
                return true;
            }
            
            // Check if we've exceeded max attempts
            return attemptData.count < CONFIG.maxReloadAttempts;
            
        } catch (e) {
            // If sessionStorage fails, allow reload
            return true;
        }
    }

    /**
     * Record reload attempt
     */
    function recordReloadAttempt() {
        try {
            const existing = sessionStorage.getItem(CONFIG.storageKey);
            let attemptData;
            
            if (existing) {
                attemptData = JSON.parse(existing);
                attemptData.count++;
                attemptData.timestamp = Date.now();
            } else {
                attemptData = {
                    count: 1,
                    timestamp: Date.now()
                };
            }
            
            sessionStorage.setItem(CONFIG.storageKey, JSON.stringify(attemptData));
        } catch (e) {
            console.warn('Could not record reload attempt:', e);
        }
    }

    /**
     * Clear cache and reload
     */
    function clearCacheAndReload() {
        console.log('Chunk load error detected. Clearing cache and reloading...');
        
        // Record this attempt
        recordReloadAttempt();
        
        // Clear various types of storage
        try {
            // Clear localStorage (except our reload tracking)
            const reloadData = sessionStorage.getItem(CONFIG.storageKey);
            localStorage.clear();
            
            // Clear cookies for this domain (optional - be careful with this)
            // Uncomment if needed:
            // document.cookie.split(";").forEach(function(c) { 
            //     document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            // });
            
        } catch (e) {
            console.warn('Error clearing storage:', e);
        }
        
        // Use cache-busting reload
        setTimeout(() => {
            // Try hard reload first (Ctrl+F5 equivalent)
            if (window.location.reload) {
                window.location.reload(true);
            } else {
                // Fallback: add cache-busting parameter
                const url = new URL(window.location.href);
                url.searchParams.set('_cb', Date.now());
                window.location.href = url.toString();
            }
        }, CONFIG.reloadDelay);
    }

    /**
     * Check if error is a chunk load error
     */
    function isChunkLoadError(error) {
        const errorMessage = error.message || error.reason?.message || '';
        const errorString = error.toString();
        
        // Common chunk load error patterns
        const chunkErrorPatterns = [
            /Loading chunk [\d]+ failed/i,
            /ChunkLoadError/i,
            /Failed to fetch dynamically imported module/i,
            /Importing a module script failed/i,
            /error loading dynamically imported module/i,
            /NetworkError when attempting to fetch resource/i
        ];
        
        return chunkErrorPatterns.some(pattern => 
            pattern.test(errorMessage) || pattern.test(errorString)
        );
    }

    /**
     * Handle errors
     */
    function handleError(error) {
        if (isChunkLoadError(error)) {
            if (shouldAttemptReload()) {
                clearCacheAndReload();
            } else {
                console.error('Multiple chunk load errors detected. Manual intervention may be required.');
                // Optional: Show user-friendly message
                alert('There was a problem loading the page. Please manually clear your browser cache or contact support if the issue persists.');
            }
        }
    }

    // Listen for unhandled errors (global error handler)
    window.addEventListener('error', (event) => {
        handleError(event.error || event);
    }, true);

    // Listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        handleError(event.reason);
    });

    // console.log('Chunk error handler initialized');
})();